import React from "react";
import { Helmet } from "react-helmet";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Video, Users, Monitor, Calendar, Mail, Play, ArrowRight } from "lucide-react";

const WebinarsDemos = () => {
  const [email, setEmail] = React.useState("");

  const handleNotifyMe = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement email subscription
    console.log("Email subscription:", email);
    setEmail("");
  };

  const upcomingSessions = [
    {
      title: "AI-Powered Patient Monitoring: Live Demo",
      description: "See our real-time monitoring solutions in action with live demonstrations and Q&A",
      type: "Interactive Demo",
      icon: Monitor,
      duration: "45 min",
      audience: "Clinical Teams"
    },
    {
      title: "Implementing AI in Healthcare: Best Practices Webinar",
      description: "Learn from healthcare leaders who've successfully integrated AI into their workflows",
      type: "Educational Webinar",
      icon: Users,
      duration: "60 min",
      audience: "Healthcare Executives"
    },
    {
      title: "ROI of Healthcare AI: Financial Impact Deep Dive",
      description: "Comprehensive analysis of costs, benefits, and financial outcomes of AI implementation",
      type: "Strategic Session",
      icon: Video,
      duration: "75 min",
      audience: "C-Suite & Finance"
    }
  ];

  const demoTopics = [
    "Real-time patient vitals monitoring",
    "Predictive analytics dashboards",
    "EHR integration workflows",
    "AI-powered alert systems",
    "Clinical decision support tools",
    "Wearable device data integration"
  ];

  return (
    <PageContainer>
      <Helmet>
        <title>Webinars & Demos - Coming Soon | Interactive Healthcare AI Learning</title>
        <meta name="description" content="Join live webinars and interactive demos showcasing AI-powered healthcare solutions. Educational sessions and product demonstrations coming soon." />
        <link rel="canonical" href="/resources/webinars-demos" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center mb-6">
              <Clock className="h-8 w-8 text-primary mr-3" />
              <span className="text-primary font-semibold">Interactive Learning Coming Soon</span>
            </div>
            
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              See AI in Action
              <span className="block bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent">
                Live Demos & Webinars
              </span>
            </h1>
            
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Join interactive webinars and live product demonstrations. Learn from healthcare experts, 
              see our solutions in real-time, and get your questions answered by our team.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <form onSubmit={handleNotifyMe} className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Get notified of upcoming sessions"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="min-w-[350px]"
                  required
                />
                <Button type="submit" className="px-6">
                  <Calendar className="h-4 w-4 mr-2" />
                  Reserve Spot
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Sessions */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Upcoming Sessions</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Interactive learning experiences designed for different roles in healthcare. 
                Choose sessions that match your interests and responsibilities.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {upcomingSessions.map((session, index) => (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 bg-primary/10 rounded-lg">
                        <session.icon className="h-6 w-6 text-primary" />
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-primary">{session.type}</div>
                        <div className="text-sm text-muted-foreground">{session.duration}</div>
                      </div>
                    </div>
                    <CardTitle className="text-xl group-hover:text-primary transition-colors">
                      {session.title}
                    </CardTitle>
                    <CardDescription className="text-base">
                      {session.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">For: {session.audience}</span>
                      <Button variant="outline" size="sm" className="group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <Play className="h-4 w-4 mr-2" />
                        Join Session
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Demo Features */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-6">What You'll Experience</h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Our interactive demos and webinars provide hands-on experience with our AI-powered 
                  healthcare solutions. See real data, actual workflows, and measurable outcomes.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center p-4 bg-muted/30 rounded-lg">
                    <Play className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-semibold">Live Product Demonstrations</div>
                      <div className="text-sm text-muted-foreground">Real-time walkthroughs of our platform</div>
                    </div>
                  </div>
                  <div className="flex items-center p-4 bg-muted/30 rounded-lg">
                    <Users className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-semibold">Interactive Q&A Sessions</div>
                      <div className="text-sm text-muted-foreground">Direct access to our clinical experts</div>
                    </div>
                  </div>
                  <div className="flex items-center p-4 bg-muted/30 rounded-lg">
                    <Monitor className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                    <div>
                      <div className="font-semibold">Screen Sharing & Collaboration</div>
                      <div className="text-sm text-muted-foreground">Follow along with live demonstrations</div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-8 rounded-2xl">
                <h3 className="text-2xl font-bold mb-6">Demo Topics Include</h3>
                <div className="grid grid-cols-1 gap-3">
                  {demoTopics.map((topic, index) => (
                    <div key={index} className="flex items-center">
                      <div className="w-2 h-2 bg-primary rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-muted-foreground">{topic}</span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-8 p-4 bg-white/50 rounded-lg">
                  <div className="font-semibold mb-2">Session Formats:</div>
                  <div className="text-sm space-y-1">
                    <div>• 30-min focused demos</div>
                    <div>• 60-min educational webinars</div>
                    <div>• 90-min deep-dive sessions</div>
                    <div>• Custom enterprise demonstrations</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule Preview */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Flexible Scheduling</h2>
            <p className="text-lg text-muted-foreground mb-8">
              We're planning sessions across different time zones and formats to accommodate 
              healthcare professionals' busy schedules.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="p-6 bg-white rounded-lg shadow-sm">
                <Calendar className="h-8 w-8 text-primary mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Weekly Sessions</h3>
                <p className="text-sm text-muted-foreground">Regular educational webinars every Tuesday and Thursday</p>
              </div>
              <div className="p-6 bg-white rounded-lg shadow-sm">
                <Video className="h-8 w-8 text-primary mx-auto mb-4" />
                <h3 className="font-semibold mb-2">On-Demand Demos</h3>
                <p className="text-sm text-muted-foreground">Schedule private demonstrations at your convenience</p>
              </div>
              <div className="p-6 bg-white rounded-lg shadow-sm">
                <Monitor className="h-8 w-8 text-primary mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Recorded Sessions</h3>
                <p className="text-sm text-muted-foreground">Access library of past webinars and demos</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Reserve Your Spot</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Be among the first to experience our interactive demos and educational webinars. 
              Limited seats available for premium sessions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="px-8">
                <Calendar className="h-5 w-5 mr-2" />
                Get Session Notifications
              </Button>
              <Button variant="outline" size="lg" className="px-8">
                Request Private Demo
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </PageContainer>
  );
};

export default WebinarsDemos;